import Context from "context/Global";
import { priceParser } from "helpers/StringParser";
import React, { useContext, useEffect, useState } from "react";
import Api from "services/Api/Api";
import {
    numberOfCustomerWarnings,
    numberOfInsurances,
    numberOfInvoicedShipments,
} from "../Helpers";
import Orders from "./Orders";
import toast from "react-hot-toast";

function roundToTwo(num) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
}

function Customer(props) {
    const context = useContext(Context);
    const [customer, setCustomer] = useState(props.customer);
    const [orders, setOrders] = useState(props.customer.orders);
    const [creatingInvoice, setCreatingInvoice] = useState(false);
    const [invoiceCreated, setInvoiceCreated] = useState(false);
    const [expandRows, setExpandRows] = useState(false);
    const [shipmentCost, setShipmentCost] = useState(0);
    const [partnerCost, setPartnerCost] = useState(0);
    const [customerPrice, setCustomerPrice] = useState(0);

    // useEffect(() => {
    // 	if(props.customer?.orders) {
    // 		setOrders(props.customer.orders)
    // 	}
    // }, [])

    function sortOrders(orderA, orderB) {
        if (orderA.invoiceIds?.length && !orderB.invoiceIds?.length) {
            return 1;
        }
        if (orderB.invoiceIds?.length && !orderA.invoiceIds?.length) {
            return -1;
        }
        return 0;
    }

    useEffect(() => {
        if (props.orders) {
            setOrders(props.orders.sort(sortOrders));
            updateStats(props.orders);
        }
    }, [props.orders]);

    function ordersChanged(orders) {
        props.onChange({
            id: customer.id,
            name: customer.name,
            clientNumber: customer.clientNumber,
            orders: orders,
        });

        updateStats(orders);
    }

    function updateStats(orders) {
        const { totalShipmentCost, totalPartnerCost, totalCustomerPrice } =
            orders.reduce(
                (acc, order) => {
                    const customerPrice = roundToTwo(
                        order.rows.reduce((cost, row) => {
                            return (
                                (parseFloat(cost) || 0) +
                                (parseFloat(row.cost) || 0)
                            );
                        }, order.customerTransportPrice),
                    );
                    return {
                        totalShipmentCost:
                            acc.totalShipmentCost +
                            (order.totalShipmentCost || 0),
                        totalPartnerCost:
                            acc.totalPartnerCost + (order.partnerCost || 0),
                        totalCustomerPrice:
                            acc.totalCustomerPrice + customerPrice,
                    };
                },
                {
                    totalPartnerCost: 0,
                    totalCustomerPrice: 0,
                    totalShipmentCost: 0,
                },
            );

        setShipmentCost(roundToTwo(totalShipmentCost || 0));
        setPartnerCost(roundToTwo(totalPartnerCost || 0));
        setCustomerPrice(roundToTwo(totalCustomerPrice || 0));
    }

    function toggle() {
        setExpandRows((expandRows) => {
            return !expandRows;
        });
    }

    async function createInvoice() {
        try {
            setCreatingInvoice(true);
            await Api.createInvoiceV2({
                customerId: customer.id,
                orders: orders,
            });
            setInvoiceCreated(true);
        } catch (error) {
            setInvoiceCreated(false);
            toast.error(error.response.data.message);
        } finally {
            setCreatingInvoice(false);
        }
    }

    function getResult() {
        return Math.round(customerPrice - partnerCost - shipmentCost);
    }

    function getWarnings() {
        const invoicedShipments = numberOfInvoicedShipments({
            orders: orders,
        });
        const warnings = numberOfCustomerWarnings({
            orders: orders,
        });
        const insurances = numberOfInsurances({
            orders: orders,
        });
        let response = [];
        if (warnings) {
            response.push(
                <div className="warnings" key="warnings">
                    <div className="text">
                        En eller flera av försändelserna har redan blivit
                        fakturerade för i en annan faktura.
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M12 5.177l8.631 15.823h-17.262l8.631-15.823zm0-4.177l-12 22h24l-12-22zm-1 9h2v6h-2v-6zm1 9.75c-.689 0-1.25-.56-1.25-1.25s.561-1.25 1.25-1.25 1.25.56 1.25 1.25-.561 1.25-1.25 1.25z"
                        />
                    </svg>
                </div>,
            );
        }
        if (invoicedShipments) {
            if (invoicedShipments !== orders.length) {
                response.push(
                    <div className="errors" key="errors">
                        <div className="text">
                            Endast vissa försändelser har fakturerats!
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fill="currentColor"
                                d="M12 5.177l8.631 15.823h-17.262l8.631-15.823zm0-4.177l-12 22h24l-12-22zm-1 9h2v6h-2v-6zm1 9.75c-.689 0-1.25-.56-1.25-1.25s.561-1.25 1.25-1.25 1.25.56 1.25 1.25-.561 1.25-1.25 1.25z"
                            />
                        </svg>
                    </div>,
                );
            } else {
                response.push(
                    <div className="done" key="done">
                        <div className="text">
                            Alla försändelser är redan fakturerade.
                        </div>
                        <svg
                            width="24"
                            height="24"
                            xmlns="http://www.w3.org/2000/svg"
                            fillRule="evenodd"
                            clipRule="evenodd"
                        >
                            <path
                                fill="currentColor"
                                d="M9.484 15.696l-.711-.696-2.552 2.607-1.539-1.452-.698.709 2.25 2.136 3.25-3.304zm0-5l-.711-.696-2.552 2.607-1.539-1.452-.698.709 2.25 2.136 3.25-3.304zm0-5l-.711-.696-2.552 2.607-1.539-1.452-.698.709 2.25 2.136 3.25-3.304zm10.516 11.304h-8v1h8v-1zm0-5h-8v1h8v-1zm0-5h-8v1h8v-1zm4-5h-24v20h24v-20zm-1 19h-22v-18h22v18z"
                            />
                        </svg>
                    </div>,
                );
            }
        }
        if (insurances) {
            response.push(
                <div className="insurances" key="insurances">
                    <div className="text">
                        {insurances}{" "}
                        {insurances > 1
                            ? "försändelser är försäkrade"
                            : "försändelse är försäkrad"}
                        .
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M15.762 8.047l-4.381 4.475-2.215-2.123-1.236 1.239 3.451 3.362 5.619-5.715-1.238-1.238zm-3.762-5.503c2.5 1.805 4.555 2.292 7 2.416v9.575c0 3.042-1.686 3.827-7 7.107-5.309-3.278-7-4.065-7-7.107v-9.575c2.447-.124 4.5-.611 7-2.416zm0-2.544c-3.371 2.866-5.484 3-9 3v11.535c0 4.603 3.203 5.804 9 9.465 5.797-3.661 9-4.862 9-9.465v-11.535c-3.516 0-5.629-.134-9-3z"
                        />
                    </svg>
                </div>,
            );
        }
        return response;
    }

    return (
        <div className="customer">
            <div className="header">
                <div className="title">{customer.name}</div>
                <div className="information">{getWarnings()}</div>
                <div className="icon"></div>
                <div className="icon">
                    <div className="hover-tooltip">
                        <div className="text">Total kostnad</div>
                    </div>
                    <svg
                        width="24"
                        height="24"
                        xmlns="http://www.w3.org/2000/svg"
                        fillRule="evenodd"
                        clipRule="evenodd"
                    >
                        <path d="M5 11v1h8v-7h-10v-1c0-.552.448-1 1-1h10c.552 0 1 .448 1 1v2h4.667c1.117 0 1.6.576 1.936 1.107.594.94 1.536 2.432 2.109 3.378.188.312.288.67.288 1.035v4.48c0 1.089-.743 2-2 2h-1c0 1.656-1.344 3-3 3s-3-1.344-3-3h-4c0 1.656-1.344 3-3 3s-3-1.344-3-3h-1c-.552 0-1-.448-1-1v-6h-2v-2h7v2h-3zm3 5.8c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm10 0c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-3-2.8h-10v2h.765c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h5.53c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h1.765v-4.575l-1.711-2.929c-.179-.307-.508-.496-.863-.496h-4.426v6zm1-5v3h5l-1.427-2.496c-.178-.312-.509-.504-.868-.504h-2.705zm-16-3h8v2h-8v-2z" />
                    </svg>
                    {priceParser(shipmentCost)}
                </div>
                <div className="icon">
                    <div className="hover-tooltip">
                        <div className="text">Partnerkostnad</div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M0 12.562l1.932-7.562 3.526.891-1.974 7.562-3.484-.891zm18.415.902c.125.287.187.598.155.91-.079.829-.698 1.448-1.457 1.602-.254.533-.733.887-1.285 1.002-.244.512-.722.89-1.296 1.01-.325.668-.97 1.012-1.674 1.012-.516 0-1.004-.183-1.356-.538-.928.404-1.902-.048-2.232-.863-.596-.068-1.107-.452-1.332-.997-.599-.071-1.114-.458-1.34-1.003-1.188-.138-1.848-1.44-1.198-2.495-.233-.058-.494-.104-.751-.152l.383-1.464c.524.1 1.01.219 1.453.358.913-.655 2.151-.295 2.549.679.608.069 1.116.464 1.334 1 .598.068 1.111.451 1.335.998.738.082 1.36.653 1.449 1.434l.002.225.45.402c.252.291.68.324.96.106.286-.223.324-.624.075-.909l-1.457-1.279c-.157-.139.052-.38.213-.241l1.491 1.308c.257.294.692.332.969.114.285-.22.316-.631.068-.916l-1.896-1.628c-.162-.135.048-.38.208-.242l1.944 1.669c.248.282.678.335.967.114.283-.22.349-.606-.002-.995-1.24-1.112-2.671-2.405-4.143-3.796-.355.488-2.176 1.502-3.279 1.502s-1.779-.675-1.96-1.343c-.157-.582.051-1.139.532-1.42.535-.313 1.055-.761 1.562-1.268-.789-.586-1.203-.398-2.067.013-.503.238-1.1.521-1.854.647l.437-1.67c1.327-.488 2.549-1.608 4.505-.083l.491-.552c.395-.447.911-.715 1.503-.715.436 0 .91.161 1.408.417 1.518.793 2.293 1.256 3.443 1.294l.394 1.508h-.008c-1.797.033-2.676-.508-4.516-1.47-.513-.263-.859-.318-1.1-.044-.984 1.12-2.031 2.309-3.192 3.063.573.458 2.019-.458 2.592-.92.25-.201.638-.468 1.128-.468.553 0 .955.331 1.244.619.68.68 2.57 2.389 3.407 3.142.434-.242.868-.435 1.311-.605l.383 1.467c-.319.134-.633.286-.95.461zm-11.037.875l.609-.747c.25-.3.215-.722-.08-.944-.296-.223-.737-.158-.986.14l-.61.749c-.251.298-.214.721.08.942s.737.159.987-.14zm1.328 1.006l.617-.755c.248-.297.213-.722-.082-.943-.294-.221-.734-.159-.984.142l-.616.754c-.251.3-.21.712.086.936.297.222.729.167.979-.134zm1.343.992l.608-.747c.251-.299.215-.721-.08-.944-.296-.222-.735-.157-.986.142l-.609.745c-.251.3-.213.724.082.945.293.221.734.16.985-.141zm1.865-.691c-.294-.224-.735-.159-.987.139l-.612.751c-.249.299-.213.722.082.943.295.221.735.16.986-.142l.61-.75c.253-.297.217-.72-.079-.941zm1.427 1.134l-.24-.212c-.063.239-.173.464-.332.65l-.358.441c.133.106.288.176.448.176.149 0 .295-.046.415-.138.284-.223.317-.632.067-.917zm5.201-10.889l1.974 7.562 3.484-.891-1.932-7.562-3.526.891z"
                        />
                    </svg>
                    {priceParser(partnerCost)}
                </div>
                <div className="icon">
                    <div className="hover-tooltip">
                        <div className="text">Kundens pris</div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M10.81 11.885h-1.098l.066-.739h1.069l-.037.739zm.613 1.099h1.14l-.007-.739h-1.111l-.022.739zm.085-2.971l-.021.739h1.054l-.007-.739h-1.026zm-1.894 2.971h1.14l.038-.739h-1.112l-.066.739zm1.291-2.971h-1.026l-.066.739h1.054l.038-.739zm6.308 0l.137.739h1.054l-.165-.739h-1.026zm-4.115 0l.023.739h1.054l-.051-.739h-1.026zm1.155 1.872l-.051-.739h-1.069l.022.739h1.098zm3.309-4.885h-7.406l-.204 2h8.058l-.448-2zm-6.107 4.885h1.097l-.007-.739h-1.069l-.021.739zm2.874 1.099l-.051-.739h-1.111l.023.739h1.139zm-5.888-2.971h-1.025l-.134.739h1.054l.105-.739zm10.215 1.872l-.165-.739h-1.069l.136.739h1.098zm-12.074-.739h-1.069l-.179.739h1.097l.151-.739zm.231-1.133h-1.026l-.179.739h1.054l.151-.739zm17.187 4.987v7h-24v-7h24zm-2 2h-20v3h20v-3zm-9 1h-2v1h2v-1zm-7.932-5.016h1.14l.15-.739h-1.111l-.179.739zm11.702-2.233l-.121-.739h-1.026l.093.739h1.054zm.185 1.134l-.121-.739h-1.069l.093.739h1.097zm1.946 1.099l-.165-.739h-2.833l.093.739h2.905zm-10.622-1.838h-1.069l-.134.739h1.098l.105-.739zm-1.402 1.838h1.139l.105-.739h-1.11l-.134.739zm-5.595.016l2.621-9h1.921c.112-.622.322-1.371.668-2h3.427c-.26.57-.499 1.259-.627 2h10.805l2.592 9h-2.221l-1.804-7h-9.42c.071.836.178 1.511-.107 2h-3.456c.292-.431.166-1.111.086-2h-.481l-1.739 7h-2.265zm5.101-6.631h2.298c-.157-1.076-.092-2.782.404-3.786h-2.249c-.553 1.006-.624 2.64-.453 3.786z"
                        />
                    </svg>
                    {priceParser(customerPrice)}
                </div>
                <div
                    className={`icon ${
                        getResult() > 0 ? "is-positive" : "is-negative"
                    }`}
                >
                    <div className="hover-tooltip">
                        <div className="text">Resultat</div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm4 14.083c0-2.145-2.232-2.742-3.943-3.546-1.039-.54-.908-1.829.581-1.916.826-.05 1.675.195 2.443.465l.362-1.647c-.907-.276-1.719-.402-2.443-.421v-1.018h-1v1.067c-1.945.267-2.984 1.487-2.984 2.85 0 2.438 2.847 2.81 3.778 3.243 1.27.568 1.035 1.75-.114 2.011-.997.226-2.269-.168-3.225-.54l-.455 1.644c.894.462 1.965.708 3 .727v.998h1v-1.053c1.657-.232 3.002-1.146 3-2.864z"
                        />
                    </svg>
                    {priceParser(getResult())}
                </div>
                <div className="icon">
                    <div className="hover-tooltip">
                        <div className="text">Antalet försändelser</div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M22 6v16h-16v-16h16zm2-2h-20v20h20v-20zm-24 17v-21h21v2h-19v19h-2z"
                        />
                    </svg>
                    {orders.length}
                </div>
                <div className="createInvoice">
                    {invoiceCreated && (
                        <div className="cell">
                            <div className="c-button c-button--text">
                                Fakturerad
                            </div>
                        </div>
                    )}
                    {!invoiceCreated && (
                        <div className="cell">
                            <div
                                className={`c-button c-button--raised ${
                                    creatingInvoice ||
                                    !orders.length ||
                                    !customer.clientNumber
                                        ? "is-disabled"
                                        : ""
                                }`}
                                onClick={() => createInvoice()}
                            >
                                {customer.clientNumber
                                    ? "Skapa faktura"
                                    : "Saknas kundnummer"}
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className={`toggle ${expandRows ? "is-expanded" : ""}`}
                    onClick={toggle}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z"
                        />
                    </svg>
                </div>
            </div>
            {expandRows && (
                <Orders
                    orders={orders}
                    onChange={ordersChanged}
                    moveOrder={props.moveOrder}
                    disabled={invoiceCreated || creatingInvoice}
                />
            )}
        </div>
    );
}

export default Customer;
